import { Box, Container, Grid, Typography } from "@mui/material";
import PortfolioMap from "../components/PortfolioMap";
import { CSSProperties, useContext, useRef, useState } from "react";
import RopTable, {
  TableRowType,
} from "../../../main/components/RopTable/RopTable";
import { useNavigate } from "react-router-dom";
import { DashboardContext } from "../../dashboard/context/DashboardContexProvider";
import { MainContext } from "../../../main/context/MainContextProvider";
import { isNumberNullOrNegative } from "../../../main/utils/utils";
import CardBox from "../../../main/components/CardBox";
import { formatNumberCurrencyBRL } from "../../dashboard/helpers/format-number";
import MovingIcon from '@mui/icons-material/Moving';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { RopChart } from "../../../main/components/RopChart";
import PortfolioEvolutionChart from "../components/PortfolioEvolutionChart";
import SwapVertIcon from '@mui/icons-material/SwapVert';
import PrimaryBaseButton from "../../../main/components/PrimaryBaseButton";


export default function Portfolio() {
  const { portfolioData } = useContext(DashboardContext);
  const { setSelectedDashboardUsina: setSelectedUsina, clientUsinas } = useContext(MainContext);
  const usinas = portfolioData.usinas;

  const elementRef = useRef(null);

  const navigate = useNavigate();

  const tableRows: TableRowType[] = usinas.map((usina) => {
    return {
      rowItems: [
        {
          item: usina.name,
          isMobile: true,
        },
        {
          item: parsePortfolioValue(usina.potenciaAtiva, "MW"),
          align: "center",
        },
        {
          item: parsePortfolioValue(usina.energiaGerada, "MWh"),
          isMobile: true,
          align: "center",
        },
        {
          item: parsePortfolioValue(
            usina.energiaIdeal, "MWh"
          ),
          align: "center",
        },
        {
          item: (isNumberNullOrNegative(usina.performanceRatio) || (Number(usina.performanceRatio) > 1.1)) ? "-" : parsePortfolioPRValue(usina.performanceRatio),
          align: "center",
        },
        {
          item: parsePortfolioValue(
            usina.prRealizado ? usina.prRealizado * 100 : null,
            "%"
          ),
          align: "center",
        },
        {
          item: parsePortfolioValue(usina.disponibilidade, "%"),
          align: "center",
        },
      ],
      onClick: () => {
        navigate(`/dashboard`);
        const selectedUsina = clientUsinas?.find(
          (u) => u.usinaIds.id == usina.id
        );
        setSelectedUsina(selectedUsina);
      },
    };
  });

  const [isAscendingOrder, setIsAscendingOrder] =
    useState(false);

  const usinasWithEnergiaGerada = usinas.filter((usina) => (usina.energiaGerada || 0) > 0)

  const topFiveEnergiaGeradaUsinas = usinasWithEnergiaGerada.sort((a, b) => (b.energiaGerada || 0) - (a.energiaGerada || 0))
    .slice(0, 5);

  const lastFiveEnergiaGeradaUsinas = usinasWithEnergiaGerada.sort((a, b) => (a.energiaGerada || 0) - (b.energiaGerada || 0))
    .slice(0, 5);

  const EnergiaGeradaChartOption = {
    title: {
      text: "Ranking de Usinas",
      textStyle: {
        fontSize: 12,
        fontWeight: "bold",
      },
    },
    tooltip: {
      trigger: "item",
      formatter: function (params: any) {
        if (Array.isArray(params)) params = params[0];

        const usinaName = isAscendingOrder
          ? topFiveEnergiaGeradaUsinas[params.dataIndex]?.name
          : lastFiveEnergiaGeradaUsinas[params.dataIndex]?.name;

        const valorFormatado = typeof params.value === "number"
          ? params.value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : params.value;

        return `<strong>${usinaName} - ${params.name}</strong><br/>${params.seriesName}: ${valorFormatado} MWh`;
      }
    },
    backgroundColor: "transparent",
    grid: {
      left: '3%',
      right: '3%',
      bottom: '3%',
      top: '20%'
    },
    xAxis: [
      {
        type: 'category',
        data: isAscendingOrder ? topFiveEnergiaGeradaUsinas.map((usina) => usina.code) : lastFiveEnergiaGeradaUsinas.map((usina) => usina.code),
        axisTick: {
          alignWithLabel: true
        }
      }
    ],
    yAxis: [
      {
        name: "MWh",
        type: 'value'
      }
    ],
    series: [
      {
        name: "Energia Gerada (MWh)",
        type: "bar",
        itemStyle: {
          color: "#27AE60",
        },
        data: isAscendingOrder ? topFiveEnergiaGeradaUsinas.map((usina) => usina.energiaGerada ?? 0) : lastFiveEnergiaGeradaUsinas.map((usina) => usina.energiaGerada ?? 0)
      }
    ]
  };

  return (
    <Grid container>
      <Grid
        sx={{ display: "flex", flexDirection: { xs: "column", md: "row" } }}
        item
        xs={12}
      >
        <Grid
          item
          xs={12}
          md={5}
          sx={{
            height: "100%",
            width: "100%",
            transition: "width 0.2s",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "350px",
          }}
        >
          <PortfolioMap usinas={usinas} />
        </Grid>
        <Grid item xs={12} md={7}>
          <Grid
            sx={{
              height: "100%",
              minHeight: "335px",
              marginTop: { xs: "15px", md: "0px" },
            }}
            container
          >
            <Grid xs={12} container>
              <Grid xs={12} lg={5} item>
                <CardBox sx={{ height: "100%", display: "flex", flexDirection: "column", width: "100%", padding: "15px", rowGap: "12px" }}>
                  <Box sx={{ width: "100%" }}>
                    <TitleAndDescriptionText title="Resumo Operacional" description="Dados gerais do portfólio" />
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "row", width: "100%" }}>
                    <TitleAndDescriptionText width="50%" title={parsePortfolioValue(portfolioData.energiaGeradaTotal, "MWh")} description="Energia gerada hoje" />
                    <TitleAndDescriptionText title={parsePortfolioValue(portfolioData.disponibilidade, "%")} description="Disponibilidade Média" />
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "row", width: "100%" }}>
                    <TitleAndDescriptionText width="50%" title={parsePortfolioValue(portfolioData.performanceRatio, "%")} description="Performance Ratio" />
                    <TitleAndDescriptionText title={formatNumberCurrencyBRL(Number(portfolioData.receitaEstimada) ?? 0)} description="Receita Estimada" />
                  </Box>
                </CardBox></Grid>
              <Grid xs={12} md={6} lg={3} item>
                <CardBox sx={{ height: "100%", display: "flex", flexDirection: "column", width: "100%", padding: "15px", justifyContent: "flex-start" }}>
                  <Box sx={{ width: "100%" }}>
                    <TitleAndDescriptionText title="Condições Climáticas" description="Impacto ambiental na geração" />
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "row", width: "100%", marginTop: "25px" }}>
                    <TitleAndDescriptionText width="50%" title={parsePortfolioValue(portfolioData.irradianciaMedia, "W/m²")} description="Irradiância" icon={<WbSunnyOutlinedIcon />} />
                    <TitleAndDescriptionText title={parsePortfolioValue(portfolioData.temperaturaMedia, "°C")} description="Temperatura" icon={<MovingIcon />} />
                  </Box>
                </CardBox></Grid>
              <Grid xs={12} md={6} lg={4} item>
                <CardBox sx={{ height: "100%", display: "flex", flexDirection: "column", width: "100%", padding: "15px", justifyContent: "flex-start" }}>
                  <Box sx={{ width: "100%" }}>
                    <TitleAndDescriptionText title="Alertas Críticos" description="Atenção necessária" />
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", width: "100%", marginTop: "15px", justifyContent: "space-evenly" }}>
                    <AlertInfo description="Inversores offline" value={Number(portfolioData.inversoresOffline) ?? 0} icon={(style) => <InfoOutlinedIcon style={style} />} color="danger" />
                    <AlertInfo description="Eventos de baixa eficiência detectados" value={Number(portfolioData.inversoresBaixaEficiencia) ?? 0} icon={(style) => <NotificationsNoneOutlinedIcon style={style} />} color="warning" />
                  </Box>
                </CardBox></Grid>
            </Grid>
            <Grid xs={12} container>
              <Grid item xs={12} lg={6}>
                <CardBox sx={{ width: "100%", height: "100%" }}>
                  <PortfolioEvolutionChart
                    energiaGeradaAtDayByHour={Array.isArray(portfolioData.energiaGeradaAtDayByHour) ? portfolioData.energiaGeradaAtDayByHour : []}
                    irradianciaMediaAtMinutes={Array.isArray(portfolioData.irradianciaMediaAtMinutes) ? portfolioData.irradianciaMediaAtMinutes : []}
                    irradianciaMediaByHour={Array.isArray(portfolioData.irradianciaMediaByHour) ? portfolioData.irradianciaMediaByHour : []}
                    potenciaAtivaAtMinutes={Array.isArray(portfolioData.potenciaAtivaAtMinutes) ? portfolioData.potenciaAtivaAtMinutes : []}
                  />
                </CardBox>
              </Grid>
              <Grid item xs={12} lg={6}>
                <CardBox sx={{ width: "100%", height: "100%", paddingTop: "20px" }}>
                  <Box sx={{ width: "100%", display: "flex", justifyContent: "space-around" }}>
                    <PrimaryBaseButton
                      startIcon={<SwapVertIcon />}
                      fontSize="15px"
                      isActive={
                        isAscendingOrder
                      }
                      onClick={() =>
                        setIsAscendingOrder(true)
                      }
                      sx={{ height: "25px" }}
                      title="Ordem crescente"
                    />
                    <PrimaryBaseButton
                      startIcon={<SwapVertIcon />}
                      fontSize="15px"
                      isActive={
                        !isAscendingOrder
                      }
                      onClick={() =>
                        setIsAscendingOrder(false)
                      }
                      sx={{ height: "25px" }}
                      title="Ordem decrescente"
                    />
                  </Box>
                  <Container
                    ref={elementRef}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      height: { xxs: "90%", md: "393px", lg: "90%" },
                      width: "100%"
                    }}>
                    <RopChart option={EnergiaGeradaChartOption} />
                  </Container>
                </CardBox>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid sx={{ marginTop: { xs: "15px", md: "0px" } }} container>
        <RopTable
          sxCardBox={{ marginLeft: { xs: "10px", md: "0px" }, width: "100%" }}
          hover
          itemsHeaders={[
            {
              item: "Usina",
              isMobile: true,
            },
            {
              item: "Potência Ativa",
              align: "center",
            },
            {
              item: "Energia gerada",
              align: "center",
              isMobile: true,
            },
            {
              item: "Energia Ideal",
              align: "center",
            },
            {
              item: "Performance Ratio",
              align: "center",
            },
            {
              item: "PR Realizado",
              align: "center",
            },
            {
              item: "Disponibilidade",
              align: "center",
            },
          ]}
          itemRows={tableRows}
        />
      </Grid>
    </Grid>
  );
}

export function parsePortfolioValue(
  value: string | number | null,
  suffix?: string
) {
  if (value === null) {
    return "---";
  }
  if (typeof value === "number") {
    return `${value.toFixed(2)} ${suffix}`;
  }
  return value;
}

export function parsePortfolioPRValue(value: string | number | null) {
  if (value === null) {
    return "---";
  }
  if (typeof value === "number") {
    return `${value.toFixed(2)}`;
  }
  return value;
}

export function TitleAndDescriptionText(props: { title: string, description: string | null, icon?: React.ReactNode, width?: string }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "start",
        justifyContent: "center",
        width: props.width
      }}
    >
      <Box>{props.icon}</Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.875rem",
        }}
      >
        {props.title}
      </Typography>
      <Typography
        sx={{
          fontWeight: 300,
          fontSize: "0.8rem",
          textAlign: "left",
        }}
      >
        {props.description ?? "Não informado"}
      </Typography>
    </Box>
  )
}

export function AlertInfo(props: { value: number, description: string | null, icon?: (style: CSSProperties) => React.ReactNode, color: "danger" | "warning" }) {
  const color = props.color == "danger" ? "#FF0000" : "#FF9F0C"

  const iconStyle: CSSProperties = {
    color: color,
    marginRight: "8px",
    width: "1.2rem",
    height: "1.2rem",
  };
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-end",
        width: "100%"
      }}
    >
      {props.icon &&
        <Box>{props.icon(iconStyle)}</Box>
      }
      <Typography
        sx={{
          fontSize: "0.875rem",
          textAlign: "left",
          color: color,
          marginRight: "3px"
        }}
      >
        {props.value > 0 ? props.value : 0}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.875rem",
          textAlign: "left",
          color: color
        }}
      >
        {props.description ?? "Não informado"}
      </Typography>
    </Box>
  )
}